import { useCallback, useEffect, useState } from "react";
import { api, API_BASE, formatApiErrorDetail } from "@/lib/api";
import { usePlant } from "@/context/PlantContext";
import { localToday, formatLocalDateTime } from "@/lib/dates";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Droplets, FlaskConical, AlertTriangle, CheckCircle2, Sparkles, Save, FileDown } from "lucide-react";

const addDays = (iso, n) => { const d = new Date(`${iso}T00:00:00`); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const FLAG = { ok: "text-emerald-500", watch: "text-amber-400", over_standard: "text-red-500 font-black" };
const SPEC = { on_spec: "text-emerald-500", watch: "text-amber-400", over_spec: "text-red-500 font-black", na: "text-muted-industrial" };

function WaterCell({ row, field, onSaved }) {
  const [v, setV] = useState(String(row[field] ?? 0));
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setV(String(row[field] ?? 0)); setDirty(false); }, [row, field]);
  const save = async () => {
    try {
      const body = field === "plant_water_l" ? { plant_water_l: Number(v || 0) } : { site_water_adjust_l: Number(v || 0) };
      await api.patch(`/dockets/${row.id}/water`, body);
      setDirty(false); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="flex items-center gap-1 justify-end">
      <Input type="number" min="0" step="1" value={v} data-testid={`slump-${field}-${row.docket_no}`}
        onChange={(e) => { setV(e.target.value); setDirty(true); }} onKeyDown={(e) => e.key === "Enter" && dirty && save()}
        className="h-7 w-16 rounded-none bg-transparent border-industrial mono text-xs text-right px-1" />
      {dirty && <button type="button" data-testid={`slump-save-${field}-${row.docket_no}`} onClick={save} className="text-primary-orange"><Save className="w-3.5 h-3.5" /></button>}
    </div>
  );
}

export default function SlumpLog() {
  const { plantId } = usePlant();
  const [from, setFrom] = useState(addDays(localToday(), -13));
  const [to, setTo] = useState(localToday());
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get(`/analytics/slump-log?date_from=${from}&date_to=${to}${plantId ? `&plant_id=${plantId}` : ""}`).then((r) => setData(r.data)).catch(() => toast.error("Could not load slump log"));
  }, [from, to, plantId]);
  useEffect(() => { load(); }, [load]);

  const s = data?.summary;
  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="slump-log-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="label-tag">QUALITY · SCIENTIFIC SLUMP LOG</div>
          <h1 className="heading text-3xl font-black uppercase flex items-center gap-2"><FlaskConical className="w-7 h-7 text-primary-orange" />AI Slump Log</h1>
          <p className="text-sm text-muted-industrial mt-1">Plant vs site water on every load, live water/cement ratios and per-mix recommendations. 1 L water ≈ 1 kg.</p>
        </div>
        <div className="flex items-center gap-2">
          <Input type="date" data-testid="slump-from" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-none bg-transparent border-industrial w-40" />
          <span className="mono text-xs text-muted-industrial">→</span>
          <Input type="date" data-testid="slump-to" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-none bg-transparent border-industrial w-40" />
          <Button data-testid="slump-refresh" variant="outline" className="rounded-none" onClick={load}>Refresh</Button>
          <a data-testid="slump-export-pdf" href={`${API_BASE}/analytics/slump-log.pdf?date_from=${from}&date_to=${to}${plantId ? `&plant_id=${plantId}` : ""}${localStorage.getItem("sa_org_id") ? `&org_id_override=${localStorage.getItem("sa_org_id")}` : ""}`} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 h-9 px-3 bg-primary-orange text-white text-sm font-bold uppercase tracking-wide hover:opacity-90">
            <FileDown className="w-4 h-4" /> Quality Report PDF
          </a>
          <a data-testid="slump-oos-pdf" href={`${API_BASE}/analytics/out-of-spec.pdf?date=${to}${plantId ? `&plant_id=${plantId}` : ""}${localStorage.getItem("sa_org_id") ? `&org_id_override=${localStorage.getItem("sa_org_id")}` : ""}`} target="_blank" rel="noreferrer"
            title="End-of-day out-of-spec water loads for the 'to' date"
            className="inline-flex items-center gap-2 h-9 px-3 border border-red-500/60 text-red-500 text-sm font-bold uppercase tracking-wide hover:bg-red-500/10">
            <AlertTriangle className="w-4 h-4" /> Out-of-Spec EOD
          </a>
        </div>
      </div>

      {s && (
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3" data-testid="slump-kpis">
          {[["Loads", s.loads], ["Volume", `${s.m3} m³`], ["Avg added water", `${s.avg_added_l_per_m3} L/m³`], ["Over AS 1379", s.over_standard, s.over_standard ? "text-red-500" : "text-emerald-500"],
            ["Avg W/C drift", `${s.avg_wc_drift >= 0 ? "+" : ""}${s.avg_wc_drift}`, s.avg_wc_drift > 0.03 ? "text-red-500" : s.avg_wc_drift > 0.01 ? "text-amber-400" : "text-emerald-500"],
            ["Loads over spec", s.over_spec, s.over_spec ? "text-red-500 font-black" : "text-emerald-500"],
            ["Avg drift vs target", `${s.avg_spec_drift >= 0 ? "+" : ""}${s.avg_spec_drift}`, s.avg_spec_drift > 0.03 ? "text-red-500" : s.avg_spec_drift > 0.01 ? "text-amber-400" : "text-emerald-500"]].map(([l, v, cls]) => (
            <div key={l} className="border border-industrial bg-surface p-3">
              <div className="label-tag">{l}</div>
              <div className={`mono text-2xl font-black ${cls || ""}`}>{v}</div>
            </div>
          ))}
        </div>
      )}

      {data?.insights?.length > 0 && (
        <div className="border border-industrial bg-surface p-4 space-y-2" data-testid="slump-insights">
          <div className="label-tag flex items-center gap-1"><Sparkles className="w-3.5 h-3.5 text-primary-orange" />AI INSIGHTS</div>
          {data.insights.map((i, k) => (
            <div key={k} className={`flex items-start gap-2 text-sm ${i.level === "alert" ? "text-red-400" : i.level === "warn" ? "text-amber-400" : "text-emerald-500"}`}>
              {i.level === "ok" ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />}<span>{i.text}</span>
            </div>
          ))}
        </div>
      )}

      {data?.mix_summary?.length > 0 && (
        <div className="border border-industrial bg-surface overflow-x-auto" data-testid="slump-mix-summary">
          <table className="w-full text-sm">
            <thead><tr className="label-tag text-left border-b border-industrial"><th className="p-2">Mix</th><th className="p-2 text-right">Loads</th><th className="p-2 text-right">m³</th><th className="p-2 text-right">Avg added L/m³</th><th className="p-2 text-right">Avg site L/m³</th><th className="p-2 text-right">Target W/C</th><th className="p-2 text-right">Avg drift vs target</th><th className="p-2 text-right">Over spec</th></tr></thead>
            <tbody>
              {data.mix_summary.map((m) => (
                <tr key={m.mix_code} className="border-b border-industrial/50 mono text-xs">
                  <td className="p-2 font-bold">{m.mix_code}</td><td className="p-2 text-right">{m.loads}</td><td className="p-2 text-right">{m.m3}</td>
                  <td className="p-2 text-right">{m.avg_added_l_per_m3}</td><td className="p-2 text-right">{m.avg_site_l_per_m3}</td>
                  <td className="p-2 text-right text-sky-400 font-bold">{m.target_wc ?? "—"}</td>
                  <td className={`p-2 text-right ${m.avg_spec_drift > 0.03 ? "text-red-500 font-black" : m.avg_spec_drift > 0.01 ? "text-amber-400" : ""}`}>{m.avg_spec_drift == null ? "—" : `${m.avg_spec_drift >= 0 ? "+" : ""}${m.avg_spec_drift}`}</td>
                  <td className={`p-2 text-right ${m.over_spec ? "text-red-500 font-black" : ""}`}>{m.over_spec || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="border border-industrial bg-surface overflow-x-auto">
        <table className="w-full text-sm" data-testid="slump-table">
          <thead>
            <tr className="label-tag text-left border-b border-industrial">
              <th className="p-2">Docket</th><th className="p-2">Loaded</th><th className="p-2">Customer</th><th className="p-2">Truck</th><th className="p-2">Mix</th>
              <th className="p-2 text-right">m³</th><th className="p-2 text-right"><Droplets className="w-3 h-3 inline" /> Plant L</th><th className="p-2 text-right"><Droplets className="w-3 h-3 inline" /> Site L</th>
              <th className="p-2 text-right">Added L/m³</th><th className="p-2 text-right">Slump</th><th className="p-2 text-right">W/C target</th><th className="p-2 text-right">W/C actual</th><th className="p-2 text-right">Drift vs target</th><th className="p-2 text-center">Spec</th>
            </tr>
          </thead>
          <tbody>
            {(data?.rows || []).map((r) => (
              <tr key={r.id} className="border-b border-industrial/50 hover:bg-surface-2/40" data-testid={`slump-row-${r.docket_no}`}>
                <td className="p-2 mono text-xs font-bold">{r.docket_no}</td>
                <td className="p-2 mono text-xs">{formatLocalDateTime(r.loaded_at).slice(5)}</td>
                <td className="p-2 text-xs truncate max-w-[180px]">{r.customer_name}</td>
                <td className="p-2 mono text-xs">{r.truck_number}</td>
                <td className="p-2 mono text-xs">{r.mix_code}</td>
                <td className="p-2 mono text-xs text-right">{r.quantity_m3}</td>
                <td className="p-2"><WaterCell row={r} field="plant_water_l" onSaved={load} /></td>
                <td className="p-2"><WaterCell row={r} field="site_water_l" onSaved={load} /></td>
                <td className={`p-2 mono text-xs text-right ${FLAG[r.flag]}`} data-testid={`slump-added-${r.docket_no}`}>{r.added_l_per_m3}</td>
                <td className="p-2 mono text-xs text-right">{r.approx_slump_mm ? `${r.approx_slump_mm}/${r.target_slump_mm ?? "—"}` : r.target_slump_mm ?? "—"}</td>
                <td className="p-2 mono text-xs text-right text-sky-400 font-bold" data-testid={`slump-target-${r.docket_no}`}>{r.wc_target ?? "—"}</td>
                <td className="p-2 mono text-xs text-right font-bold" data-testid={`slump-wc-${r.docket_no}`}>{r.wc_actual ?? "—"}</td>
                <td className={`p-2 mono text-xs text-right ${SPEC[r.spec_flag]}`} data-testid={`slump-spec-drift-${r.docket_no}`}>{r.wc_spec_drift == null ? "—" : `${r.wc_spec_drift >= 0 ? "+" : ""}${r.wc_spec_drift}`}</td>
                <td className="p-2 text-center" data-testid={`slump-spec-flag-${r.docket_no}`}>
                  {r.spec_flag === "over_spec" ? <span className="mono text-[10px] font-black text-red-500 bg-red-500/10 px-1.5 py-0.5 border border-red-500/40">OVER SPEC</span>
                    : r.spec_flag === "watch" ? <span className="mono text-[10px] font-bold text-amber-400">WATCH</span>
                    : r.spec_flag === "on_spec" ? <span className="mono text-[10px] text-emerald-500">ON SPEC</span>
                    : <span className="mono text-[10px] text-muted-industrial">—</span>}
                </td>
              </tr>
            ))}
            {data && !data.rows.length && <tr><td colSpan={14} className="p-6 text-center text-muted-industrial text-sm" data-testid="slump-empty">No dockets in this range.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
